// Argus — videoStream MSE wrapper. Bloc ws-direct-batch-3 iter3 2026-05-06.
//
// Refactor cu mp4box.js (gpac/mp4box.js v2.3) pentru remux on-the-fly de la MP4 standard
// la fragmented MP4 (fMP4) compatibil MSE. Auto-detectează codec real din moov atom (avc1.X.Y.Z)
// fără manual parsing avcC/hvcC. Suportă atât MP4 fast-start cât și MP4 cu moov-at-end.
//
// Bune practici buffering aplicate:
// - Buffer ahead target 30s, trigger fetch < 15s, pause fetch > 60s.
// - SourceBuffer.remove(0, currentTime - 30) la QuotaExceededError (memory cleanup).
// - Mode 'segments' default — playback ordered by timestamps.
// - Pause/play listeners — opri fetch în timp ce video paused.
//
// API: createMseVideoSource(agentId, path, justification, options) → {srcUrl, attachVideoElement, dispose}.

import { createFile, type ISOFile, type Movie } from 'mp4box';
import { wsClient } from './wsClient';

const CHUNK_SIZE = 1 * 1024 * 1024; // 1 MB chunks din WS.
const BUFFER_AHEAD_TARGET_S = 30;
const BUFFER_AHEAD_TRIGGER_S = 15;
const BUFFER_AHEAD_PAUSE_S = 60;
const MONITOR_INTERVAL_MS = 200;
const SEGMENT_NB_SAMPLES = 30; // fragments per onSegment emission (~1s @ 30fps). Smaller =
                                // less back-fetch la seek (mp4box cere bytes ÎN TRECUT pentru
                                // sample iterator reconstruction pe MP4 nefragmented).
const PREFETCH_INTERVAL_MS = 50; // Background pre-fetch — populates cache pentru future seek.

export interface MseVideoSource {
  readonly srcUrl: string;
  attachVideoElement: (videoEl: HTMLVideoElement) => void;
  dispose: () => void;
}

export interface MseVideoSourceOptions {
  /** Apelat la fail (codec nesuportat, mp4box parse error). Caller afișează error UI. */
  onError?: (err: Error) => void;
}

export class MseInitFailedError extends Error {
  constructor(message: string) { super(message); this.name = 'MseInitFailedError'; }
}

export class MseFormatUnsupportedError extends Error {
  constructor(message: string) { super(message); this.name = 'MseFormatUnsupportedError'; }
}

export function createMseVideoSource(
  agentId: string,
  path: string,
  justification: string,
  options: MseVideoSourceOptions = {},
): MseVideoSource {
  const mediaSource = new MediaSource();
  const srcUrl = URL.createObjectURL(mediaSource);

  // keepMdatData=true — păstrează mdat în memorie pentru ca segments emise post-onReady
  // să aibă acces la sample data. Critic pentru moov-at-end MP4 unde onReady fires tardiv.
  const mp4boxFile: ISOFile<unknown, unknown> = createFile(true);
  let sourceBuffer: SourceBuffer | null = null;
  let videoEl: HTMLVideoElement | null = null;
  let fetchOffset = 0; // byte offset for next requestFileChunk
  let fileTotalSize = 0;
  let aborted = false;
  let fetchInFlight = false;
  let playbackPaused = false;
  let monitorInterval: ReturnType<typeof setInterval> | null = null;
  let appendQueue: ArrayBuffer[] = [];
  let initSegmentsAppended = false;
  // Pre-fetch background — populates cache pentru future seek-uri YouTube-style.
  // Independent de fetchAndFeed (care răspunde la mp4box requests). Avansează linear
  // de la prefetchOffset → totalSize, populând chunkCache silent.
  let prefetchOffset = 0;
  let prefetchInterval: ReturnType<typeof setInterval> | null = null;
  // Cache chunks fetched (key = byte offset, value = bytes). La seek, mp4box poate cere bytes
  // ÎNAPOI care deja au fost transmise de WS — folosim cache pentru instant return fără round-trip.
  // Memory cost: ~size_fișier (acceptabil pentru video preview, ~250 MB pentru 3090.mp4).
  const chunkCache = new Map<number, ArrayBuffer>();

  function findCachedChunk(offset: number): { ab: ArrayBuffer; offset: number; size: number } | null {
    // Linear scan — pentru ~256 chunks (1 MB each), cost neglijabil. Map<offset> e indexed pe boundary
    // exact, dar mp4box poate cere offset arbitrar din mijloc — caut chunk care acoperă offset-ul.
    for (const [chunkOffset, ab] of chunkCache) {
      if (offset >= chunkOffset && offset < chunkOffset + ab.byteLength) {
        return { ab, offset: chunkOffset, size: ab.byteLength };
      }
    }
    return null;
  }

  function logDebug(msg: string): void {
    if (typeof console !== 'undefined') console.debug(`[mseVideo] ${msg}`);
  }

  // === MP4Box.js callbacks ===

  mp4boxFile.onReady = (info: Movie) => {
    // info.tracks[i].codec = codec REAL auto-detect (ex: "avc1.640028", "mp4a.40.2").
    // Build full codec MIME din toate tracks.
    const codecs = info.tracks.map((t) => t.codec).filter((c) => c).join(', ');
    const mime = `video/mp4; codecs="${codecs}"`;
    logDebug(`onReady: codecs=${codecs}, duration=${info.duration}, tracks=${info.tracks.length}`);
    if (!MediaSource.isTypeSupported(mime)) {
      const err = new MseFormatUnsupportedError(`Codec nesuportat: ${mime}`);
      options.onError?.(err);
      cleanupOnError(err);
      return;
    }
    try {
      sourceBuffer = mediaSource.addSourceBuffer(mime);
      sourceBuffer.mode = 'segments';
      sourceBuffer.addEventListener('updateend', drainAppendQueue);
      sourceBuffer.addEventListener('error', (e) => {
        logDebug(`SourceBuffer error event: ${(e as Event).type}`);
      });
      // Setează durata MediaSource pentru ca <video> să afișeze progress bar VOD (NU live).
      // info.duration e în unități timescale; convertim la secunde.
      if (info.duration > 0 && info.timescale > 0) {
        const durationSec = info.duration / info.timescale;
        mediaSource.duration = durationSec;
        logDebug(`mediaSource.duration set to ${durationSec.toFixed(2)}s (timescale=${info.timescale})`);
      }
    } catch (err) {
      options.onError?.(err as Error);
      cleanupOnError(err as Error);
      return;
    }
    // Setup segmentation per track — emit chunks fMP4 via onSegment.
    // rapAlignement=true — segments aliniate la random access points (IDR keyframes).
    // Critic pentru ca SourceBuffer să decodeze fragmenetele.
    for (const track of info.tracks) {
      mp4boxFile.setSegmentOptions(track.id, sourceBuffer, {
        nbSamples: SEGMENT_NB_SAMPLES,
        rapAlignement: true,
      });
    }
    // Initialize: returns single fMP4 init buffer (ftyp + moov combined pentru toate tracks).
    const initSegs = mp4boxFile.initializeSegmentation();
    logDebug(`init segmentation: ${initSegs.tracks.length} tracks, init buffer ${initSegs.buffer.byteLength} bytes`);
    enqueueAppend(initSegs.buffer);
    initSegmentsAppended = true;
    drainAppendQueue();
    // Rewind cursor la 0 + start emission of fragments. Critic pentru moov-at-end MP4
    // unde mp4box a procesat deja samples linear — start fără seek poate să nu re-emită.
    try {
      mp4boxFile.seek(0, true);
    } catch (err) {
      logDebug(`seek(0) post-onReady failed (poate fi non-fatal): ${(err as Error).message}`);
    }
    mp4boxFile.start();
    logDebug('mp4box.start() called — flush pentru a forța procesare samples buffered');
    try { mp4boxFile.flush(); } catch (err) { logDebug(`flush post-start failed: ${(err as Error).message}`); }
    // Pornesc pre-fetch background — populates cache pentru seek instant viitor.
    startPrefetchBackground();
  };

  /**
   * Pre-fetch background loop. Avansează linear de la prefetchOffset → fileTotalSize,
   * populând `chunkCache` silent (NU appendBuffer la mp4box). Util pentru YouTube-style
   * seek instant la chunks deja cached.
   */
  function startPrefetchBackground(): void {
    if (prefetchInterval !== null) return;
    prefetchOffset = 0;
    prefetchInterval = setInterval(async () => {
      if (aborted) {
        if (prefetchInterval !== null) { clearInterval(prefetchInterval); prefetchInterval = null; }
        return;
      }
      if (fileTotalSize === 0 || prefetchOffset >= fileTotalSize) {
        if (prefetchInterval !== null) { clearInterval(prefetchInterval); prefetchInterval = null; }
        logDebug(`pre-fetch background complete (cache size=${chunkCache.size})`);
        return;
      }
      // Skip dacă chunk deja cached.
      const cached = findCachedChunk(prefetchOffset);
      if (cached) {
        prefetchOffset = cached.offset + cached.size;
        return;
      }
      // Fetch via WS, populate cache silent (NU appendBuffer la mp4box).
      try {
        const chunk = await wsClient.requestFileChunk(agentId, path, prefetchOffset, CHUNK_SIZE);
        if (aborted) return;
        const ab = base64ToArrayBuffer(chunk.data);
        chunkCache.set(chunk.offset, ab);
        prefetchOffset = chunk.offset + chunk.size;
      } catch (err) {
        logDebug(`pre-fetch error la offset ${prefetchOffset}: ${(err as Error).message}`);
        // Skip chunk eronat + continue.
        prefetchOffset += CHUNK_SIZE;
      }
    }, PREFETCH_INTERVAL_MS);
  }

  mp4boxFile.onSegment = (id: number, _user: unknown, buffer: ArrayBuffer, sampleNum: number, isLast: boolean) => {
    logDebug(`onSegment track=${id} sampleNum=${sampleNum} bufferSize=${buffer.byteLength} isLast=${isLast}`);
    enqueueAppend(buffer);
  };

  mp4boxFile.onError = (module: string, message: string) => {
    const err = new MseInitFailedError(`mp4box error în ${module}: ${message}`);
    logDebug(`mp4box onError: ${module}: ${message}`);
    options.onError?.(err);
    cleanupOnError(err);
  };

  // Hook moov-start pentru debug — fires la prima detection moov box.
  (mp4boxFile as { onMoovStart?: () => void }).onMoovStart = () => {
    logDebug('moov box parsing started');
  };

  // mp4box internal logging rămâne la default (warn) — debug e prea verbose (mii de linii).

  // === Append queue (serializează writes la SourceBuffer) ===

  function enqueueAppend(buffer: ArrayBuffer): void {
    appendQueue.push(buffer);
    drainAppendQueue();
  }

  function drainAppendQueue(): void {
    if (aborted || !sourceBuffer) return;
    if (sourceBuffer.updating) return;
    if (mediaSource.readyState !== 'open') return;
    const next = appendQueue.shift();
    if (!next) return;
    try {
      sourceBuffer.appendBuffer(next);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'QuotaExceededError' && videoEl) {
        // Memory pressure — remove vechi (current - 30s).
        const removeUntil = Math.max(0, videoEl.currentTime - 30);
        if (removeUntil > 0 && sourceBuffer.buffered.length > 0) {
          logDebug(`QuotaExceeded — removing buffered range 0..${removeUntil}`);
          appendQueue.unshift(next); // retry after remove
          try {
            sourceBuffer.remove(0, removeUntil);
          } catch { /* ignore */ }
          return;
        }
      }
      logDebug(`appendBuffer error: ${(err as Error).message}`);
      options.onError?.(err as Error);
      cleanupOnError(err as Error);
    }
  }

  // === Fetch loop pe baza poziției video + buffer ahead ===

  async function fetchAndFeed(): Promise<void> {
    if (aborted || fetchInFlight) return;
    if (fileTotalSize > 0 && fetchOffset >= fileTotalSize) return;
    fetchInFlight = true;
    try {
      let ab: ArrayBuffer;
      let chunkOffsetActual: number;
      let chunkSizeActual: number;

      // Check cache primul — pentru seek invers la chunks deja fetchate.
      const cached = findCachedChunk(fetchOffset);
      if (cached) {
        ab = cached.ab;
        chunkOffsetActual = cached.offset;
        chunkSizeActual = cached.size;
      } else {
        const chunk = await wsClient.requestFileChunk(
          agentId, path, fetchOffset, CHUNK_SIZE,
          fetchOffset === 0 ? justification : undefined,
        );
        if (aborted) return;
        if (fileTotalSize === 0) fileTotalSize = chunk.totalSize;
        ab = base64ToArrayBuffer(chunk.data);
        chunkOffsetActual = chunk.offset;
        chunkSizeActual = chunk.size;
        // Cache pentru future seek-uri.
        chunkCache.set(chunkOffsetActual, ab);
      }

      // Tag fileStart property pentru mp4box.
      const taggedAb = ab as ArrayBuffer & { fileStart: number };
      taggedAb.fileStart = chunkOffsetActual;
      // CRITICAL: catch separat pe mp4box.appendBuffer — pentru MP4 standard nefragmentat,
      // createFragment poate crashe la seek-uri multiple (internal state inconsistent).
      // NU propag eroarea la UI (alert alarmant); doar log + continue.
      // SourceBuffer rămâne cu segments deja appended → playback continuă pe range buffered.
      let nextStart: number | undefined;
      try {
        nextStart = mp4boxFile.appendBuffer(taggedAb, false);
      } catch (mp4err) {
        logDebug(`mp4box appendBuffer crash (silent — playback continue pe SourceBuffer.buffered): ${(mp4err as Error).message}`);
        nextStart = chunkOffsetActual + chunkSizeActual; // skip la next chunk
      }
      const advanced = chunkOffsetActual + chunkSizeActual;
      if (typeof nextStart === 'number' && nextStart > 0) {
        if (nextStart !== advanced) {
          logDebug(`mp4box requested skip to offset ${nextStart} (was at ${advanced}, cache size=${chunkCache.size})`);
        }
        fetchOffset = nextStart;
      } else {
        fetchOffset = advanced;
      }
      if (fileTotalSize > 0 && fetchOffset >= fileTotalSize) {
        logDebug(`fetch complete — toate bytes furnizate (offset=${fetchOffset}/${fileTotalSize}, cache size=${chunkCache.size}). Flush final.`);
        try { mp4boxFile.flush(); } catch { /* ignore */ }
        const flushInterval = setInterval(() => {
          if (sourceBuffer && !sourceBuffer.updating && appendQueue.length === 0) {
            clearInterval(flushInterval);
            try { if (mediaSource.readyState === 'open') mediaSource.endOfStream(); } catch { /* ignore */ }
          }
        }, 100);
      }
    } catch (err) {
      // Erori network / wsClient — log dar NU alert UI (caller poate fi în background fetch).
      logDebug(`fetch network error: ${(err as Error).message}`);
    } finally {
      fetchInFlight = false;
    }
  }

  function checkBufferAndFetch(): void {
    if (aborted || !videoEl) return;
    if (mediaSource.readyState !== 'open') {
      if (monitorInterval !== null) { clearInterval(monitorInterval); monitorInterval = null; }
      return;
    }
    if (fileTotalSize > 0 && fetchOffset >= fileTotalSize) return;
    if (!initSegmentsAppended) {
      // Înainte de init, fetch primul chunk pentru moov detection.
      void fetchAndFeed();
      return;
    }
    if (!sourceBuffer) return;
    let bufferedAhead = 0;
    try {
      if (sourceBuffer.buffered.length === 0) {
        void fetchAndFeed();
        return;
      }
      const bufferedEnd = sourceBuffer.buffered.end(sourceBuffer.buffered.length - 1);
      bufferedAhead = bufferedEnd - videoEl.currentTime;
    } catch {
      if (monitorInterval !== null) { clearInterval(monitorInterval); monitorInterval = null; }
      return;
    }
    if (bufferedAhead > BUFFER_AHEAD_PAUSE_S) return;
    if (bufferedAhead < BUFFER_AHEAD_TRIGGER_S || (!playbackPaused && bufferedAhead < BUFFER_AHEAD_TARGET_S)) {
      void fetchAndFeed();
    }
  }

  function cleanupOnError(err: Error): void {
    aborted = true;
    if (monitorInterval !== null) { clearInterval(monitorInterval); monitorInterval = null; }
    try {
      if (mediaSource.readyState === 'open') mediaSource.endOfStream('decode');
    } catch { /* ignore */ }
    logDebug(`cleanup on error: ${err.message}`);
  }

  // === Video element handlers ===

  function onSeeking(): void {
    if (!videoEl || aborted) return;
    // NU apelăm mp4boxFile.seek() — pentru MP4 standard nefragmented, mp4box internal
    // state corupt la seek-uri multiple → createFragment crash cu undefined.write.
    // În schimb, lăsăm browser-ul să facă seek nativ via SourceBuffer.buffered:
    // - Chunks deja appended la SourceBuffer (de pre-fetch + fetchAndFeed) → browser folosește.
    // - Pre-fetch background populates cache progresiv → TOATE bytes ajung la mp4box → segments
    //   eventual se acumulează în SourceBuffer.
    // Trade-off: seek instant DOAR pe range deja appended la SourceBuffer. Pentru truly
    // YouTube-style instant random seek pe MP4 nefrag, soluția arhitecturală e agent ffmpeg
    // remux la fMP4 (TODO ws-direct-batch-4).
    logDebug(`onSeeking to ${videoEl.currentTime}s — relying on SourceBuffer.buffered (no mp4box.seek)`);
  }

  function onPause(): void { playbackPaused = true; }
  function onPlay(): void { playbackPaused = false; }

  // === Kickoff la sourceopen ===

  mediaSource.addEventListener('sourceopen', () => {
    logDebug('sourceopen');
    void fetchAndFeed();
    monitorInterval = setInterval(checkBufferAndFetch, MONITOR_INTERVAL_MS);
  }, { once: true });

  return {
    srcUrl,
    attachVideoElement: (el: HTMLVideoElement) => {
      videoEl = el;
      el.addEventListener('seeking', onSeeking);
      el.addEventListener('pause', onPause);
      el.addEventListener('play', onPlay);
      try {
        if (mediaSource.readyState === 'closed') el.load();
      } catch { /* ignore */ }
    },
    dispose: () => {
      aborted = true;
      if (monitorInterval !== null) {
        clearInterval(monitorInterval);
        monitorInterval = null;
      }
      if (prefetchInterval !== null) {
        clearInterval(prefetchInterval);
        prefetchInterval = null;
      }
      if (videoEl) {
        videoEl.removeEventListener('seeking', onSeeking);
        videoEl.removeEventListener('pause', onPause);
        videoEl.removeEventListener('play', onPlay);
        try { videoEl.removeAttribute('src'); videoEl.load(); } catch { /* ignore */ }
      }
      try {
        if (mediaSource.readyState === 'open') mediaSource.endOfStream();
      } catch { /* ignore */ }
      try { URL.revokeObjectURL(srcUrl); } catch { /* ignore */ }
      appendQueue = [];
      chunkCache.clear();
    },
  };
}

function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const bin = atob(b64);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer.slice(0, bytes.byteLength) as ArrayBuffer;
}
